import {Public, Security} from "@mui/icons-material";
import {Box, Button, Card, CardContent, Chip, FormHelperText, MenuItem, Stack} from "@mui/material";
import {Checkbox, ErrorAlert, TextField} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsyncCallback} from "react-async-hook";
import {useTenantApi} from "../../api";
import {Flavor} from "@smalox/shared";
import {useLocalization} from "@smalox/shared";
import {useReloadTenant, useTenantId, useTenantOptional} from "../../tenant";

export function TenantGeneralSettings() {
	const {t} = useLocalization();
	const tenantId = useTenantId();
	const tenant = useTenantOptional();
	const {updateTenant} = useTenantApi();
	const reloadTenant = useReloadTenant();

	const [name, setName] = useState("");
	const [locale, setLocale] = useState("de");
	const [flavor, setFlavor] = useState<Flavor>("SMALOX");
	const [publiclyBookable, setPubliclyBookable] = useState(false);

	useEffect(() => {
		if (tenant) {
			setName(tenant.name);
			setLocale(tenant.locale);
			setFlavor((tenant.flavor as Flavor) ?? "SMALOX");
			setPubliclyBookable(tenant.publiclyBookable ?? false);
		}
	}, [tenant]);

	const save = useAsyncCallback(async () => {
		await updateTenant(tenantId, {
			name,
			locale,
			flavor,
			publiclyBookable,
		});
		await reloadTenant();
	});

	if (!tenant) {
		return null;
	}

	return (
		<Box maxWidth="sm">
			<Card>
				<CardContent>
					<Stack spacing={2}>
						<TextField
							label={t("tenants.name.title")}
							helperText={t("tenants.name.helperText")}
							fullWidth
							value={name}
							onChange={setName}
							required
						/>
						<TextField
							select
							label={t("tenants.locale.title")}
							helperText={t("tenants.locale.helperText")}
							fullWidth
							value={locale}
							onChange={setLocale}
							required
						>
							<MenuItem value="de">Deutsch</MenuItem>
							<MenuItem value="en">English</MenuItem>
							<MenuItem value="fr">Français</MenuItem>
						</TextField>
						<TextField
							select
							label={t("tenants.flavor.title")}
							helperText={t("tenants.flavor.helperText")}
							fullWidth
							value={flavor}
							onChange={(value) => setFlavor(value as Flavor)}
							required
						>
							<MenuItem value="SMALOX">{t("tenants.flavor.SMALOX")}</MenuItem>
							<MenuItem value="BOXLOX">{t("tenants.flavor.BOXLOX")}</MenuItem>
							<MenuItem value="DAYLOX">{t("tenants.flavor.DAYLOX")}</MenuItem>
						</TextField>
						<Box>
							<Checkbox
								label={t("tenants.publiclyBookable.title")}
								checked={publiclyBookable}
								onChange={setPubliclyBookable}
							/>
							<FormHelperText sx={{ml: 4, mt: -0.5}}>
								{t("tenants.publiclyBookable.helperText")}
							</FormHelperText>
							<Box sx={{ml: 4, mt: 1}}>
								{publiclyBookable ? (
									<Chip
										icon={<Public />}
										label={t("tenants.publiclyBookable.public")}
										color="secondary"
										variant="outlined"
										size="small"
									/>
								) : (
									<Chip
										icon={<Security />}
										label={t("tenants.publiclyBookable.private", {tenantName: tenant.name})}
										color="secondary"
										variant="outlined"
										size="small"
									/>
								)}
							</Box>
						</Box>
					</Stack>
				</CardContent>
				{save.error && (
					<CardContent>
						<ErrorAlert error={save.error} />
					</CardContent>
				)}
				<CardContent>
					<Button
						variant="outlined"
						color="primary"
						onClick={save.execute}
						disabled={save.loading}
					>
						{t("save")}
					</Button>
				</CardContent>
			</Card>
		</Box>
	);
}
